import React from 'react';
import { Chip } from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import TaskAltIcon from '@mui/icons-material/TaskAlt';

// New backend reports processing state under `contract.processing.status` with
// values: uploaded | extracting | classifying | normalizing | extracting_tasks
// | review | ready | error. Lifecycle status (`contract.status`) is the
// final state once processing.status === 'ready': draft | pending_cod |
// active | expired | terminated.
const statusConfig = {
  // ── processing.* (in-flight pipeline) ──
  uploaded: {
    label: 'Uploaded',
    color: '#2d7dd2',
    icon: <HourglassEmptyIcon sx={{ fontSize: 14, animation: 'pulse 1.5s ease-in-out infinite' }} />,
  },
  extracting: {
    label: 'OCR Extracting',
    color: '#9b59b6',
    icon: <AutorenewIcon sx={{ fontSize: 14, animation: 'spin 1.5s linear infinite' }} />,
  },
  classifying: {
    label: 'Classifying',
    color: '#d4930d',
    icon: <AutorenewIcon sx={{ fontSize: 14, animation: 'spin 1.5s linear infinite' }} />,
  },
  normalizing: {
    label: 'Extracting Terms',
    color: '#d4930d',
    icon: <AutorenewIcon sx={{ fontSize: 14, animation: 'spin 1.5s linear infinite' }} />,
  },
  extracting_tasks: {
    label: 'Extracting Tasks',
    color: '#e67e22',
    icon: <TaskAltIcon sx={{ fontSize: 14, animation: 'spin 1.5s linear infinite' }} />,
  },
  review: {
    label: 'In Review',
    color: '#d4930d',
    icon: null,
  },
  ready: {
    label: 'Ready',
    color: 'primary.main',
    icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
  },
  error: {
    label: 'Error',
    color: 'error.main',
    icon: <ErrorIcon sx={{ fontSize: 14 }} />,
  },

  // ── lifecycle status ──
  draft:        { label: 'Draft',        color: 'text.secondary', icon: null },
  pending_cod:  { label: 'Pending COD',  color: '#d4930d',        icon: null },
  active:       { label: 'Active',       color: 'primary.main',   icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
  expired:      { label: 'Expired',      color: 'text.secondary', icon: null },
  terminated:   { label: 'Terminated',   color: 'error.main',     icon: null },

  // ── legacy compatibility — keep old keys mapped while old data is around ──
  uploading:                  { label: 'Uploading',       color: '#2d7dd2', icon: <HourglassEmptyIcon sx={{ fontSize: 14, animation: 'pulse 1.5s ease-in-out infinite' }} /> },
  processing_ocr:             { label: 'OCR Processing',  color: '#9b59b6', icon: <AutorenewIcon sx={{ fontSize: 14, animation: 'spin 1.5s linear infinite' }} /> },
  processing_classification:  { label: 'Classifying',     color: '#d4930d', icon: <AutorenewIcon sx={{ fontSize: 14, animation: 'spin 1.5s linear infinite' }} /> },
  processing_extraction:      { label: 'Extracting Data', color: '#d4930d', icon: <AutorenewIcon sx={{ fontSize: 14, animation: 'spin 1.5s linear infinite' }} /> },
  completed:                  { label: 'Completed',       color: 'primary.main', icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
};

const ContractStatusChip = ({ status }) => {
  const config = statusConfig[status] || {
    label: status,
    color: 'text.secondary',
    icon: null,
  };

  return (
    <Chip
      label={config.label}
      icon={config.icon}
      size="small"
      sx={{
        bgcolor: `${config.color}12`,
        color: config.color,
        fontWeight: 600,
        fontSize: '0.72rem',
        letterSpacing: '0.02em',
        border: `1px solid ${config.color}20`,
        py: 0.25,
        '& .MuiChip-icon': {
          color: config.color,
        },
      }}
    />
  );
};

export default ContractStatusChip;
